import {
  Badge,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import {
  peakSeasonRangeLabel,
  selectPeakSeasonDates,
} from 'src/utils/peakSeasonDates.js';
import { useNzGreatWalkCancellationSummary } from 'src/hooks/useNzGreatWalkCancellationSummary.js';
import CancellationDateGrid from '../Blogs/CancellationDateGrid.jsx';

/**
 * Compact walk-level cancellation stats banner used inside the TripBuilder
 * drawer. Mirrors `TripStatsBanner`'s inline strip look, but sources data by
 * fetching per-hut stats for all huts on the walk and summing openings by
 * date. Clicking opens a modal with the same per-date grid used on the blog
 * cancellation pages.
 */
export default function NzGreatWalkTripStatsBanner({ walk, maxDates = 12 }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { view } = useNzGreatWalkCancellationSummary(walk);
  if (!view) return null;

  const { dates: upcomingCancellations = [], mode } = view.dateCounts
    ? selectPeakSeasonDates(view.dateCounts, view.peakSeason, maxDates)
    : { dates: [], mode: null };
  const isPeakSeasonView =
    !!view.peakSeason && (mode === 'peak' || mode === 'shoulder');
  const seasonLabel = isPeakSeasonView
    ? peakSeasonRangeLabel(view.peakSeason)
    : null;

  const total = view.totalSpots || 0;
  if (total <= 0 || !upcomingCancellations.length) return null;

  const displayText = view.isLast30Days
    ? `${total.toLocaleString('en-US')} ${walk.name} beds reopened in the last 30 days`
    : `${total.toLocaleString('en-US')} ${walk.name} beds reopened`;

  return (
    <>
      <HStack
        w="100%"
        py={1.5}
        px={3}
        bg="orange.50"
        borderWidth="1px"
        borderColor="orange.200"
        borderRadius="md"
        color="orange.800"
        fontSize="sm"
        fontWeight={500}
        justify="space-between"
        cursor="pointer"
        onClick={onOpen}
        _hover={{ bg: 'orange.100' }}
        transition="background 0.15s"
      >
        <Text noOfLines={1}>{displayText}</Text>
        <Text
          as="span"
          textDecoration="underline"
          fontWeight={600}
          flexShrink={0}
        >
          See stats →
        </Text>
      </HStack>

      <Modal isOpen={isOpen} onClose={onClose} size={['full', 'lg', 'xl']}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr={12}>
            <VStack align="start" spacing={1}>
              <Text fontSize={['md', 'lg']} fontWeight={700} color="orange.800">
                {walk.name} availability we&apos;ve detected
              </Text>
              {seasonLabel && (
                <Badge
                  colorScheme="orange"
                  borderRadius="full"
                  px={2.5}
                  py={0.5}
                  fontSize="xs"
                >
                  Peak season: {seasonLabel}
                </Badge>
              )}
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                <Text as="span" fontWeight="bold" color="orange.600">
                  {total} {total === 1 ? 'bed' : 'beds'}
                </Text>{' '}
                reopened{' '}
                <Text as="span" fontWeight="semibold">
                  {view.isLast30Days ? 'in the last 30 days' : 'so far'}
                </Text>{' '}
                across every {walk.name} hut we track.
              </Text>

              <CancellationDateGrid
                entries={upcomingCancellations}
                colorScheme="orange"
                unitLabel="bed"
              />

              {view.lastUpdated && (
                <Text fontSize="xs" color="gray.400" textAlign="center">
                  Updated:{' '}
                  {new Date(view.lastUpdated).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </Text>
              )}

              <Text
                fontSize={['xs', 'sm']}
                color="gray.700"
                textAlign="center"
                pt={2}
              >
                <Text as="span" fontWeight="bold" color="green.600">
                  We watch DOC — you book on DOC.
                </Text>{' '}
                Set an alert below for the nights you need.
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
